import { type FormEventHandler, useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi, profileQueryKeys, type Profile, type UpdateProfilePayload } from '@entities/profile';

import {
    mapProfileFormErrors,
    type ProfileFormErrors,
} from '../lib/mapProfileFormErrors';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';


export type ProfileFormValues = {
    name: string;
    fieldActivityId?: string | number;
    locationId?: string | number;
    phone: string;
};

const getProfileFormValues = (profile: Profile): ProfileFormValues => ({
    name: profile.user.name,
    fieldActivityId: profile.user.fieldActivity?.id,
    locationId:
        profile.context.locationType === 'city'
            ? profile.user.city?.id
            : profile.user.country?.id,
    phone: profile.user.phone ?? '',
});

export const useProfileForm = (profile: Profile) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const initializedProfileIdRef = useRef(profile.user.id);
    const [formValues, setFormValues] = useState<ProfileFormValues>(() =>
        getProfileFormValues(profile)
    );
    const [formErrors, setFormErrors] = useState<ProfileFormErrors>({});

    useEffect(() => {
        if (initializedProfileIdRef.current === profile.user.id) {
            return;
        }

        setFormValues(getProfileFormValues(profile));
        initializedProfileIdRef.current = profile.user.id;
    }, [profile]);

    const updateProfileMutation = useMutation({
        mutationFn: profileApi.updateProfile,
        onSuccess: (updatedProfile) => {
            queryClient.setQueryData(profileQueryKeys.current(), updatedProfile);
            setFormValues(getProfileFormValues(updatedProfile));
            initializedProfileIdRef.current = updatedProfile.user.id;
            setFormErrors({});
        },
        onError: (error) => {
            const responseError = isAxiosError<IDataResponseError>(error)
                ? error.response?.data
                : undefined;

            setFormErrors({
                ...mapProfileFormErrors(responseError?.errors),
                form: responseError?.message ?? t('profile.page.main.errors.save'),
            });
        },
    });

    const activityAreaOptions = profile.context.fieldActivities.map(({ id, name }) => ({
        value: id,
        label: name,
    }));
    const locationOptions =
        profile.context.locationType === 'city'
            ? profile.context.cities.map(({ id, name, region, country }) => ({
                  value: id,
                  label: `${region.name}, ${country.name}, ${name}`,
              }))
            : profile.context.countries.map(({ id, name, flagUnicode }) => ({
                  value: id,
                  label: `${flagUnicode} ${name}`,
              }));

    const updateFormValue = <TName extends keyof ProfileFormValues>(
        name: TName,
        value: ProfileFormValues[TName]
    ) => {
        setFormErrors((currentErrors) => ({
            ...currentErrors,
            [name]: undefined,
            form: undefined,
        }));
        setFormValues((currentValues) => ({ ...currentValues, [name]: value }));
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();

        const locationId = Number(formValues.locationId);
        const payload: UpdateProfilePayload = {
            name: formValues.name,
            'field_activity_id': Number(formValues.fieldActivityId),
            ...(profile.context.locationType === 'city'
                ? { 'city_id': locationId }
                : { 'country_id': locationId }),
        };

        setFormErrors({});
        updateProfileMutation.mutate(payload);
    };

    return {
        t,
        formValues,
        formErrors,
        activityAreaOptions,
        locationOptions,
        isPending: updateProfileMutation.isPending,
        updateFormValue,
        handleSubmit,
    };
};
